import { createProductIcon } from './product-icon.js';
import { createProductActions } from './product-actions.js';
import { positionPreview } from '../core/preview-position.js';
import { isControlActivation } from '../core/navigation.js';

const closeDelay = 140;

/** One reusable preview per instance, outside the scrollable bar container. */
export function createProductPreview(host, onAction) {
  const document = host.ownerDocument;
  const window = document.defaultView;
  const root = host.shadowRoot;
  const bar = root.querySelector('.positive-nav-bar');
  const node = (tag, name) => {
    const element = document.createElement(tag);
    element.className = `positive-nav-preview${name ? `-${name}`: ''}`;
    return element;
  };
  const element = node('section');
  element.id = `${host.instanceId}-preview`;
  element.hidden = true;
  element.setAttribute('role', 'region');
  const header = node('div', 'header');
  const icon = node('span', 'icon');
  const heading = node('div', 'heading');
  const name = node('span', 'name');
  name.id = `${element.id}-name`;
  const category = node('p', 'category');
  const description = node('p', 'description');
  description.id = `${element.id}-description`;
  element.setAttribute('aria-labelledby', name.id);
  heading.append(name, category);
  header.append(icon, heading);
  const actions = createProductActions(document);
  const { primary, secondary } = actions;
  element.append(header, description, actions.element);
  root.append(element);

  let catalog;
  let t;
  let active;
  let pointer;
  let pointerInCard = false;
  let dismissed;
  let iconId;
  let closeTimer;
  let frame;
  let animation;
  let listening = false;
  let disposed = false;
  let suspended = false;

  const getProduct = (trigger) => catalog?.visibleProducts.find(({ id, isCurrent }) =>
    id === trigger?.dataset.productId && !isCurrent);
  const getTrigger = (target) => {
    const trigger = target?.closest?.('.positive-nav-product.positive-nav-button');
    return trigger && bar.contains(trigger) ? trigger: null;
  };
  const hasFocus = () => !!active && (root.activeElement === active || element.contains(root.activeElement));
  const clearClose = () => { window.clearTimeout(closeTimer); closeTimer = undefined; };
  const clearPosition = () => {
    if (frame !== undefined) window.cancelAnimationFrame(frame);
    frame = undefined;
  };
  const stopAnimation = () => {
    if (!animation) return;
    animation.onfinish = null;
    animation.cancel();
    animation = undefined;
  };
  const viewport = () => {
    const visual = window.visualViewport;
    return {
      left: visual?.offsetLeft ?? 0, top: visual?.offsetTop ?? 0,
      width: visual?.width ?? document.documentElement.clientWidth,
      height: visual?.height ?? document.documentElement.clientHeight,
    };
  };
  const length = (style, key, fallback) => {
    const value = Number.parseFloat(style.getPropertyValue(`--positive-nav-preview-${key}`)
      || style.getPropertyValue(`--positive-nav-default-preview-${key}`));
    return Number.isFinite(value) ? Math.max(0, value): fallback;
  };

  function reposition() {
    if (!active || disposed) return;
    // Measure the final placement without any active animation translation.
    stopAnimation();
    const trigger = active.getBoundingClientRect();
    const barRect = bar.getBoundingClientRect();
    const view = viewport();
    if (!host.isConnected || !trigger.width || !trigger.height
      || trigger.right <= Math.max(barRect.left, view.left)
      || trigger.left >= Math.min(barRect.right, view.left + view.width)
      || barRect.bottom <= view.top || barRect.top >= view.top + view.height) {
      close();
      return;
    }
    const style = window.getComputedStyle(element);
    const margin = length(style, 'viewport-padding', 8);
    const gap = length(style, 'offset', 8);
    element.style.maxWidth = `${Math.max(0, view.width - margin * 2)}px`;
    element.style.maxHeight = `${Math.max(0, view.height - margin * 2)}px`;
    const size = element.getBoundingClientRect();
    const position = positionPreview({ left: trigger.left, top: barRect.top, bottom: barRect.bottom }, size, view, { gap, margin });
    const origin = element.dataset.positioning === 'viewport' ? { left: 0, top: 0 }: host.getBoundingClientRect();
    element.style.left = `${position.left - origin.left}px`;
    element.style.top = `${position.top - origin.top}px`;
    element.style.maxHeight = `${position.maxHeight}px`;
    element.dataset.placement = position.placement;
  }

  function animate(previous) {
    if (typeof element.animate !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const style = window.getComputedStyle(element);
    const key = previous ? 'switch-duration': 'motion-duration';
    const value = (style.getPropertyValue(`--positive-nav-preview-${key}`)
      || style.getPropertyValue(`--positive-nav-default-preview-${key}`)).trim();
    const duration = Number.parseFloat(value) * (value.endsWith('ms') ? 1: value.endsWith('s') ? 1000: 0);
    if (!Number.isFinite(duration) || duration <= 0) return;
    const easing = (style.getPropertyValue('--positive-nav-preview-motion-easing')
      || style.getPropertyValue('--positive-nav-default-preview-motion-easing')).trim();
    const target = element.getBoundingClientRect();
    const distance = length(style, 'motion-distance', 6);
    const x = previous ? previous.bounds.left - target.left: 0;
    const y = previous ? previous.bounds.top - target.top: (element.dataset.placement === 'top' ? distance: -distance);
    const motion = element.animate([
      { transform: `translate(${x}px, ${y}px)`, opacity: previous ? previous.opacity: 0 },
      { transform: 'translate(0px, 0px)', opacity: 1 },
    ], {
      duration,
      easing: window.CSS.supports('animation-timing-function', easing) ? easing: 'ease-out',
    });
    animation = motion;
    motion.onfinish = () => { if (animation === motion) animation = undefined; };
  }

  function schedulePosition(event) {
    if ((event?.target?.nodeType && element.contains(event.target)) || frame !== undefined) return;
    frame = window.requestAnimationFrame(() => { frame = undefined; reposition(); });
  }
  function outsidePointer(event) {
    const path = event.composedPath();
    if (!path.includes(element) && !path.includes(active)) close({ dismiss: true });
  }
  function escape(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    close({ dismiss: true, restoreFocus: element.contains(root.activeElement) });
  }
  function visibility() { if (document.hidden) close(); }
  function blur() { close({ dismiss: true }); }
  function listen(enabled) {
    if (listening === enabled) return;
    listening = enabled;
    const method = enabled ? 'addEventListener': 'removeEventListener';
    // Bar scrolling does not cross the Shadow DOM boundary.
    bar[method]('scroll', schedulePosition);
    window[method]('scroll', schedulePosition, true);
    window[method]('resize', schedulePosition);
    window[method]('blur', blur);
    window.visualViewport?.[method]('resize', schedulePosition);
    window.visualViewport?.[method]('scroll', schedulePosition);
    document[method]('pointerdown', outsidePointer, true);
    document[method]('keydown', escape);
    document[method]('visibilitychange', visibility);
  }

  function close({ dismiss = false, restoreFocus = false } = {}) {
    clearClose();
    clearPosition();
    stopAnimation();
    const previous = active;
    if (dismiss) dismissed = previous ?? pointer;
    active = undefined;
    pointerInCard = false;
    previous?.setAttribute('aria-expanded', 'false');
    previous?.removeAttribute('aria-describedby');
    if (element.hasAttribute('popover') && element.matches(':popover-open')) element.hidePopover();
    element.hidden = true;
    delete element.dataset.previewProduct;
    listen(false);
    if (restoreFocus && host.isConnected) (previous?.isConnected ? previous: bar).focus({ preventScroll: true });
  }

  function render(product) {
    if (iconId !== product.id) {
      icon.replaceChildren(createProductIcon(product.logoId, document));
      iconId = product.id;
    }
    name.textContent = product.name;
    category.textContent = product.category;
    description.textContent = product.description;
    actions.update(product, t, catalog.currentProduct?.id);
  }

  function open(trigger) {
    clearClose();
    const product = getProduct(trigger);
    if (disposed || suspended || !host.isConnected || !product || dismissed === trigger) return;
    const changed = active !== trigger;
    // Start from the visible position, including during rapid product switching.
    const previous = changed && active ? {
      bounds: element.getBoundingClientRect(), opacity: Number.parseFloat(window.getComputedStyle(element).opacity),
    }: undefined;
    if (changed) {
      clearPosition();
      active?.setAttribute('aria-expanded', 'false');
      active?.removeAttribute('aria-describedby');
      active = trigger;
      pointerInCard = false;
    }
    render(product);
    element.dataset.previewProduct = product.id;
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-describedby', description.id);
    element.hidden = false;
    if (typeof element.showPopover === 'function') {
      element.setAttribute('popover', 'manual');
      element.dataset.positioning = 'viewport';
      if (!element.matches(':popover-open')) element.showPopover();
    } else {
      element.removeAttribute('popover');
      element.dataset.positioning = 'host';
    }
    reposition();
    if (active) {
      listen(true);
      if (changed) animate(previous);
    }
  }

  function scheduleClose() {
    clearClose();
    if (!active || hasFocus() || pointer === active || pointerInCard) return;
    closeTimer = window.setTimeout(() => {
      closeTimer = undefined;
      if (!hasFocus() && pointer !== active && !pointerInCard) close();
    }, closeDelay);
  }
  function pointerOver(event) {
    if (suspended || event.pointerType === 'touch') return;
    const trigger = getTrigger(event.target);
    if (!trigger || trigger.contains(event.relatedTarget)) return;
    pointer = trigger;
    clearClose();
    if (dismissed === trigger || element.contains(root.activeElement)) return;
    if (active === trigger) return;
    open(trigger);
  }
  function pointerDown(event) {
    if (event.pointerType !== 'touch') return;
    close();
    dismissed = getTrigger(event.target);
  }
  function pointerOut(event) {
    const trigger = getTrigger(event.target);
    if (!trigger || trigger.contains(event.relatedTarget)) return;
    if (pointer === trigger) pointer = undefined;
    if (dismissed === trigger) dismissed = undefined;
    if (element.contains(event.relatedTarget)) { clearClose(); return; }
    scheduleClose();
  }
  function cardEnter(event) { if (event.pointerType !== 'touch') { pointerInCard = true; clearClose(); } }
  function cardLeave(event) {
    pointerInCard = false;
    if (active?.contains(event.relatedTarget)) return;
    scheduleClose();
  }
  function focusIn(event) {
    if (suspended) return;
    const trigger = getTrigger(event.target);
    if (trigger) { if (dismissed !== trigger) { dismissed = undefined; open(trigger); } }
    else if (element.contains(event.target)) clearClose();
    else { dismissed = undefined; close(); }
  }
  function focusOut() {
    // Wait for the next focusin, including control moves during an update.
    window.queueMicrotask(() => {
      if (!disposed && !hasFocus()) { dismissed = undefined; scheduleClose(); }
    });
  }
  function keyboard(event) {
    if (!active || event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
    const focused = root.activeElement;
    const links = [primary.link, secondary.link].filter((link) => !link.hidden);
    if (focused === active && !event.shiftKey) {
      event.preventDefault(); links[0].focus();
    } else if (focused === links[0] && event.shiftKey) {
      event.preventDefault(); active.focus();
    } else if (focused === links[links.length - 1] && !event.shiftKey) {
      const controls = [...bar.querySelectorAll('button, a[href]')].filter((control) => !control.closest('[hidden]'));
      const next = controls[controls.indexOf(active) + 1];
      if (next) { event.preventDefault(); next.focus(); }
      else close({ dismiss: true, restoreFocus: true });
    }
  }
  function activate(event) {
    const link = event.target.closest?.('a[data-destination]');
    if (!link || !element.contains(link) || event.defaultPrevented || !isControlActivation(event, link)) return;
    const product = getProduct(active);
    if (!product) return;
    const destination = link.dataset.destination;
    const action = { action: 'navigate', source: 'preview', productId: product.id,
      destination, url: product.urls[destination], target: '_blank' };
    close({ dismiss: true, restoreFocus: true });
    onAction(action, event);
  }

  bar.addEventListener('pointerover', pointerOver);
  bar.addEventListener('pointerout', pointerOut);
  bar.addEventListener('pointerdown', pointerDown);
  element.addEventListener('pointerenter', cardEnter);
  element.addEventListener('pointerleave', cardLeave);
  element.addEventListener('click', activate);
  element.addEventListener('auxclick', activate);
  root.addEventListener('focusin', focusIn);
  root.addEventListener('focusout', focusOut);
  root.addEventListener('keydown', keyboard);

  return {
    element,
    close,
    suspend(value) {
      suspended = value;
      if (value) close({ dismiss: true });
      else dismissed = getTrigger(root.activeElement) ?? pointer;
    },
    update(nextCatalog, translate) {
      catalog = nextCatalog;
      t = translate;
      if (active) {
        if (!active.isConnected) {
          const replacement = bar.querySelector(`[data-product-id="${active.dataset.productId}"]`);
          if (replacement) {
            const previous = active;
            active = replacement;
            if (pointer === previous) pointer = replacement;
            previous.setAttribute('aria-expanded', 'false');
            previous.removeAttribute('aria-describedby');
            active.setAttribute('aria-expanded', 'true');
            active.setAttribute('aria-describedby', description.id);
          }
        }
        const product = getProduct(active);
        if (!product) close({ restoreFocus: element.contains(root.activeElement) });
        else { render(product); reposition(); }
      }
    },
    dispose() {
      close();
      disposed = true;
      bar.removeEventListener('pointerover', pointerOver);
      bar.removeEventListener('pointerout', pointerOut);
      bar.removeEventListener('pointerdown', pointerDown);
      element.removeEventListener('pointerenter', cardEnter);
      element.removeEventListener('pointerleave', cardLeave);
      element.removeEventListener('click', activate);
      element.removeEventListener('auxclick', activate);
      root.removeEventListener('focusin', focusIn);
      root.removeEventListener('focusout', focusOut);
      root.removeEventListener('keydown', keyboard);
      element.remove();
      catalog = undefined;
      t = undefined;
    },
  };
}
